import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { NumberStepper } from "@ui/components/NumberStepper";
import { OutlineButton } from "@ui/components/OutlineButton";
import { Select } from "@ui/components/Select";
import { ToggleChip, ToggleChipGroup } from "@ui/components/ToggleChip";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { useState } from "react";
import type { Household } from "../../apply-household";
import { tierLabel } from "./labels";

const MAX_ADULTS = 8;
const MAX_CHILDREN = 8;
const MAX_NIGHTS = 365;
const CHILD_AGES = Array.from({ length: 18 }, (_, age) => age);
/** The age a newly added child starts at — mid-primary, the middle of the range. */
const DEFAULT_CHILD_AGE = 6;
const TIERS: Household["tier"][] = ["low", "mid", "high"];

interface Child {
  id: string;
  age: number;
}

let nextChildId = 0;

function asChild(age: number): Child {
  nextChildId += 1;
  return { id: `child-${nextChildId}`, age };
}

const householdControl = tv({
  slots: {
    root: "flex flex-col gap-5 rounded-3xl p-5 shadow-keyline-inset",
    title: "font-semibold text-base text-neutral-900",
    field: "flex flex-col gap-2",
    label: "font-medium text-neutral-500 text-xs uppercase tracking-wider",
    children: "flex flex-wrap items-center gap-2",
    child: "flex items-center gap-1",
    remove:
      "cursor-pointer text-neutral-400 outline-none hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-purple-500",
    actions: "flex justify-end",
  },
});

export interface HouseholdControlProps {
  household: Household;
  /** Called once, on commit — the quote is a round trip, not a keystroke. */
  onCommit: (household: Household) => void;
  isPending?: boolean;
}

/**
 * Who the figure is for. Edited locally and committed in one go: every change
 * re-quotes the place against the panel, so a stepper that fired per press
 * would ask the server eight questions to answer one.
 */
export function HouseholdControl({ household, isPending = false, onCommit }: HouseholdControlProps) {
  const styles = householdControl();
  const [adults, setAdults] = useState(household.adults);
  const [nights, setNights] = useState(household.nights);
  const [tier, setTier] = useState(household.tier);
  // Children carry an identity of their own, not their position: two children
  // aged ten are two children, and a row keyed by age would swap them under
  // React the moment one of them had a birthday.
  const [children, setChildren] = useState<Child[]>(() => household.child_ages.map(asChild));

  const draft: Household = { ...household, adults, nights, tier, child_ages: children.map((child) => child.age) };
  const dirty = JSON.stringify(draft) !== JSON.stringify(household);

  return (
    <div className={styles.root()}>
      <h3 className={styles.title()}>{t("components.views.destinations.costs.household.title")}</h3>

      <div className={styles.field()}>
        <span className={styles.label()}>{t("components.views.destinations.costs.household.adults")}</span>
        <NumberStepper
          aria-label={t("components.views.destinations.costs.household.adults")}
          max={MAX_ADULTS}
          min={1}
          onChange={setAdults}
          value={adults}
        />
      </div>

      <div className={styles.field()}>
        <span className={styles.label()}>{t("components.views.destinations.costs.household.children")}</span>
        <div className={styles.children()}>
          {children.map((child) => (
            <span className={styles.child()} key={child.id}>
              <Select
                aria-label={t("components.views.destinations.costs.household.child_age", { age: child.age })}
                onSelectionChange={(key) =>
                  setChildren(
                    children.map((current) => (current.id === child.id ? { ...current, age: Number(key) } : current)),
                  )
                }
                selectedKey={String(child.age)}
                size="sm"
              >
                {CHILD_AGES.map((option) => (
                  <Select.Item id={String(option)} key={option} textValue={String(option)}>
                    {t("components.views.destinations.costs.household.child_age", { age: option })}
                  </Select.Item>
                ))}
              </Select>
              <Button
                aria-label={t("components.views.destinations.costs.household.remove_child", { age: child.age })}
                className={styles.remove()}
                onPress={() => setChildren(children.filter((current) => current.id !== child.id))}
              >
                <Icon name="minus" size="12" />
              </Button>
            </span>
          ))}
          {children.length < MAX_CHILDREN && (
            <OutlineButton onPress={() => setChildren([...children, asChild(DEFAULT_CHILD_AGE)])} size="36">
              {t("components.views.destinations.costs.household.add_child")}
            </OutlineButton>
          )}
        </div>
      </div>

      <div className={styles.field()}>
        <span className={styles.label()}>{t("components.views.destinations.costs.household.nights")}</span>
        <NumberStepper
          aria-label={t("components.views.destinations.costs.household.nights")}
          max={MAX_NIGHTS}
          min={1}
          onChange={setNights}
          value={nights}
        />
      </div>

      <div className={styles.field()}>
        <span className={styles.label()}>{t("components.views.destinations.costs.household.tier")}</span>
        <ToggleChipGroup
          aria-label={t("components.views.destinations.costs.household.tier")}
          className="grid-cols-3"
          disallowEmptySelection
          onSelectionChange={(keys) => {
            const [selected] = [...keys];
            if (selected) setTier(selected as Household["tier"]);
          }}
          selectedKeys={[tier]}
          selectionMode="single"
        >
          {TIERS.map((tier) => (
            <ToggleChip id={tier} key={tier}>
              {tierLabel(tier)}
            </ToggleChip>
          ))}
        </ToggleChipGroup>
      </div>

      <div className={styles.actions()}>
        <OutlineButton isDisabled={!dirty || isPending} onPress={() => onCommit(draft)}>
          {t("components.views.destinations.costs.household.update")}
        </OutlineButton>
      </div>
    </div>
  );
}
